/**
 * The pet screen ([Pets](../../../docs/PETS.md#the-pet-screen)).
 *
 * A child's creatures, their food, and any egg still on its way. Three things
 * here are the design rather than layout choices
 * ([D-105](../../../docs/DECISIONS.md#d-105)):
 *
 * - **A hungry pet is napping, and it says so in those words.** Never sad,
 *   never hungry-with-a-sad-face, never a countdown bar draining towards
 *   something bad. The standard virtual-pet screen runs on low-grade guilt
 *   aimed at children, and refusing it is mostly a matter of what this file is
 *   allowed to say.
 * - **The bonus is written in plain words, from the pet's own card.** *"Sprout
 *   gives you 2 extra practice words in every lesson"*, not *"+2 review items"*.
 *   A nine-year-old deciding which pet to take out is the whole interaction,
 *   and a spreadsheet is not a decision.
 * - **The egg counts lessons, not hours.** `says` comes from the server, which
 *   derives it from a lesson count — there is no timestamp anywhere in the
 *   chain to render a countdown from even if somebody wanted one.
 *
 * Swapping pets is optimistic and feeding is not: a swap is free and instant,
 * while feeding spends something, and a screen that shows food gone before the
 * server agreed it was spent would be lying about a thing a child paid for.
 */

import { useCallback, useEffect, useState } from 'react';
import { type Client, type Trouble, troubleFrom } from './api.js';
import Sneakers from './Sneakers.js';

interface PetCard {
  readonly id: string;
  readonly nameL1: string;
  readonly emoji: string;
  readonly rarity: string;
  readonly says: string;
  readonly active: boolean;
  readonly mood: 'happy' | 'napping';
  readonly daysLeft: number;
  readonly mirroring: string | null;
}

interface EggCard {
  readonly id: string;
  readonly kind: string;
  readonly done: number;
  readonly needed: number;
  readonly ready: boolean;
  readonly says: string;
}

interface Shelf {
  readonly playDay: number;
  readonly pets: readonly PetCard[];
  readonly eggs: readonly EggCard[];
  readonly food: readonly { readonly id: string; readonly qty: number }[];
  readonly bonus: string | null;
}

/** The foods, in the words and pictures a child sees. Local, never from a response. */
const FOOD_NAMES: Readonly<Record<string, { emoji: string; name: string }>> = {
  berries: { emoji: '🍓', name: 'Berries' },
  nuts: { emoji: '🌰', name: 'Nuts' },
  honeycomb: { emoji: '🍯', name: 'Honeycomb' },
  starfruit: { emoji: '⭐', name: 'Star Fruit' },
};

const EGG_EMOJI = '🥚';

/**
 * How the state line reads.
 *
 * Napping is the whole of it — no second clause. The first version said
 * *"Napping · Napping until you feed them"*, which only showed up in a real
 * browser screenshot: the mood and the food line each said the word, and
 * together they read like nagging. Sneakers already offers the feed, and the
 * button below is called *Feed Pebble*, so saying it a third time here is what
 * turns a calm screen into a pestering one.
 */
const stateLine = (mood: 'happy' | 'napping', days: number): string =>
  mood === 'napping'
    ? 'Napping'
    : days === 1
      ? 'Happy · food for 1 more play-day'
      : `Happy · food for ${String(days)} more play-days`;

export default function Pets({ client }: { readonly client: Client }) {
  const [shelf, setShelf] = useState<Shelf | null>(null);
  const [trouble, setTrouble] = useState<Trouble | null>(null);
  const [busy, setBusy] = useState(false);
  /** Bumped to re-read after anything that changes the shelf. */
  const [turn, setTurn] = useState(0);

  useEffect(() => {
    let live = true;
    // Wrapped, because a server one deploy behind has no `pets` router at all
    // and the call throws synchronously ([D-066](../../../docs/DECISIONS.md#d-066)).
    Promise.resolve()
      .then(() => client.pets.shelf.query())
      .then((next) => {
        if (live) setShelf(next);
      })
      .catch((error: unknown) => {
        if (live) setTrouble(troubleFrom(error));
      });
    return () => {
      live = false;
    };
  }, [client, turn]);

  const again = useCallback(() => {
    setTurn((n) => n + 1);
  }, []);

  const activate = useCallback(
    (petId: string) => {
      // Optimistic: swapping is free and instant, and a pet that waits for a
      // round trip before it looks chosen feels like it might not have worked.
      setShelf((was) =>
        was === null
          ? was
          : {
              ...was,
              pets: was.pets.map((pet) => ({ ...pet, active: pet.id === petId })),
            },
      );
      void Promise.resolve()
        .then(() => client.pets.activate.mutate({ petId }))
        .then(again)
        .catch(again);
    },
    [client, again],
  );

  const feed = useCallback(
    (petId: string, foodId: string) => {
      // Not optimistic. Feeding spends something, and showing it gone before
      // the server said so would be the screen lying about what a child paid.
      setBusy(true);
      void Promise.resolve()
        .then(() => client.pets.feed.mutate({ petId, foodId }))
        .then(() => {
          setBusy(false);
          again();
        })
        .catch(() => {
          setBusy(false);
          again();
        });
    },
    [client, again],
  );

  const open = useCallback(
    (eggId: string) => {
      setBusy(true);
      void Promise.resolve()
        .then(() => client.pets.hatch.mutate({ eggId }))
        .then(() => {
          setBusy(false);
          again();
        })
        .catch(() => {
          setBusy(false);
          again();
        });
    },
    [client, again],
  );

  if (trouble !== null) {
    return (
      <div className="grow pad center">
        <p className="sub" role="alert">
          {trouble.kind === 'offline'
            ? 'No connection. Is the wi-fi on?'
            : 'Your pets would not load.'}
        </p>
      </div>
    );
  }

  if (shelf === null) {
    return (
      <div className="grow pad center">
        <p className="sub">Finding your pets…</p>
      </div>
    );
  }

  const active = shelf.pets.find((pet) => pet.active) ?? null;
  const others = shelf.pets.filter((pet) => !pet.active);
  const nothing = shelf.pets.length === 0 && shelf.eggs.length === 0;

  return (
    <div className="grow pad">
      <Sneakers
        text={
          nothing
            ? 'No pets yet! Silver chests and up have eggs in them.'
            : active === null
              ? 'Pick one to come along with you.'
              : active.mood === 'happy'
                ? `${active.nameL1} is happy to see you!`
                : `${active.nameL1} is having a nap. Feed them when you like.`
        }
        audioRef={null}
        size={72}
      />

      {nothing && (
        <p className="sub" style={{ marginTop: 8 }}>
          Keep playing — an egg turns up in a silver chest, and it hatches after a few
          more lessons.
        </p>
      )}

      {shelf.eggs.length > 0 && (
        <section style={{ marginTop: 14 }}>
          <h3>Eggs</h3>
          {shelf.eggs.map((egg) => (
            <div key={egg.id} className="card" style={{ marginTop: 8 }}>
              <div className="row" style={{ gap: 10, alignItems: 'center' }}>
                <span aria-hidden="true" style={{ fontSize: 34 }}>
                  {EGG_EMOJI}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 800 }}>{egg.says}</div>
                  {/*
                    The app's own bar, not a raw `<progress>`: that renders in
                    the browser's default blue, which is nothing like anything
                    else on the screen. Only visible in a real browser — happy-dom
                    loads no CSS, so the component test could not have told me.

                    A lesson counter, never a clock. The label says what it is
                    counting, so a screen reader reads "3 of 5 lessons" rather
                    than a bare number with no units.
                  */}
                  <div
                    className="bar"
                    style={{ height: 9, marginTop: 6 }}
                    role="progressbar"
                    aria-valuenow={egg.done}
                    aria-valuemin={0}
                    aria-valuemax={egg.needed}
                    aria-label={`${String(egg.done)} of ${String(egg.needed)} lessons`}
                  >
                    <i
                      style={{
                        width: `${String(Math.round((egg.done / Math.max(1, egg.needed)) * 100))}%`,
                      }}
                    />
                  </div>
                </div>
              </div>
              {egg.ready && (
                <button
                  className="btn"
                  type="button"
                  disabled={busy}
                  style={{ marginTop: 10 }}
                  onClick={() => {
                    open(egg.id);
                  }}
                >
                  Open it!
                </button>
              )}
            </div>
          ))}
        </section>
      )}

      {active !== null && (
        <section style={{ marginTop: 14 }}>
          <h3>Out with you</h3>
          <div className="card" style={{ marginTop: 8 }}>
            <div className="row" style={{ gap: 12, alignItems: 'center' }}>
              <span aria-hidden="true" style={{ fontSize: 48 }}>
                {active.emoji}
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 800, fontSize: 18 }}>{active.nameL1}</div>
                {/*
                  Plainly labelled, in the design's own words. "Napping" is the
                  worst thing this screen is ever allowed to say about a pet.
                */}
                <div className="tiny">{stateLine(active.mood, active.daysLeft)}</div>
              </div>
            </div>
            <p className="tiny" style={{ marginTop: 8 }}>
              {active.says}
            </p>
            {active.mood === 'napping' && (
              <p className="tiny" style={{ marginTop: 4 }}>
                Their bonus is paused while they nap. Nothing else changes.
              </p>
            )}
          </div>
        </section>
      )}

      {shelf.food.length > 0 && active !== null && (
        <section style={{ marginTop: 14 }}>
          <h3>Feed {active.nameL1}</h3>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 6 }}>
            {shelf.food.map((food) => {
              const known = FOOD_NAMES[food.id];
              if (known === undefined) return null;
              return (
                <button
                  key={food.id}
                  className="btn ghost"
                  type="button"
                  disabled={busy}
                  style={{ width: 'auto', minHeight: 48 }}
                  aria-label={`Feed ${known.name}, ${String(food.qty)} left`}
                  onClick={() => {
                    feed(active.id, food.id);
                  }}
                >
                  <span aria-hidden="true">{known.emoji}</span> {known.name} ×{food.qty}
                </button>
              );
            })}
          </div>
        </section>
      )}

      {others.length > 0 && (
        <section style={{ marginTop: 14 }}>
          <h3>Your other pets</h3>
          <p className="tiny" style={{ marginTop: 4 }}>
            Tap one to take them out instead. It is free, any time.
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
            {others.map((pet) => (
              <button
                key={pet.id}
                className="btn ghost"
                type="button"
                style={{ width: 'auto', minHeight: 48 }}
                aria-label={`Take ${pet.nameL1} out. ${pet.says}`}
                onClick={() => {
                  activate(pet.id);
                }}
              >
                <span aria-hidden="true">{pet.emoji}</span> {pet.nameL1}
              </button>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
